import React, { useMemo } from "react";
import { Table, Column, HeaderCell, Cell } from "rsuite-table";
import "rsuite-table/dist/css/rsuite-table.css";
import { ArticleViewData, ReminderView } from "../../../../types/reminderTypes";
import {
  isTherapyExpiringToday,
  isTherapyExpiringSoon,
  formatDate,
  createTherapyTooltip,
} from "../../../../utils/rfzo/dateUtils";
import { ReactComponent as BellIcon } from "../../../../assets/icons/bell-icon.svg";
import { formatRFZOPrice } from "../../../../utils/rfzo/priceUtils";
import { useUnifiedFilters } from "../../../../hooks/useUnifiedFilters";
import { useArticleSearch } from "../../../../hooks/useViewSearch";
import styles from "./ArticleView.module.scss";

interface ArticleViewProps {
  data: ArticleViewData[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const ArticleView: React.FC<ArticleViewProps> = ({
  data,
  loading,
  error,
  onRefresh,
}) => {
  // Get search term from unified filters (URL)
  const { filters } = useUnifiedFilters(ReminderView.ARTICLES);

  // Apply search at component level
  const { filteredData, totalResults, hasActiveSearch, searchTerm } =
    useArticleSearch(data, filters.search || "", loading);

  // Prepare filtered data for table display
  const tableData = useMemo(() => {
    return filteredData.map((item, index) => ({
      ...item,
      index: index + 1,
    }));
  }, [filteredData]);

  if (error) {
    return (
      <div className={styles.error}>
        <p>Došlo je do greške: {error}</p>
        <button onClick={onRefresh} className={styles.retryButton}>
          Pokušaj ponovo
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Učitavanje podataka...</span>
      </div>
    );
  }

  return (
    <div className={styles.articleView}>
      {/* Show search results info */}
      {hasActiveSearch && (
        <div className={styles.searchInfo}>
          Prikazano {totalResults} od {data.length} artikala
        </div>
      )}

      <Table
        data={tableData}
        height={600}
        autoHeight
        bordered
        cellBordered
        hover
        rowClassName={(rowData: ArticleViewData & { index: number }) =>
          rowData?.therapyTaken ? styles.therapyTakenRow : ""
        }
        renderEmpty={() => (
          <div className={styles.emptyState}>
            {searchTerm ? (
              <>Nema artikala koji odgovaraju pretrazi "{searchTerm}".</>
            ) : (
              "Nema artikala za prikaz sa trenutnim filterima."
            )}
          </div>
        )}
      >
        {/* Index */}
        <Column width={50} fixed="left" align="center">
          <HeaderCell>
            <span title="Redni broj">r. br.</span>
          </HeaderCell>
          <Cell dataKey="index" verticalAlign="middle" />
        </Column>

        {/* Due Date */}
        <Column width={120}>
          <HeaderCell>Datum dospeća</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: ArticleViewData) => (
              <span
                title={createTherapyTooltip(
                  rowData.willBeReadyFrom,
                  rowData.willBeReadyTo
                )}
                style={{ cursor: "help" }}
                className={styles.dateCell}
              >
                {formatDate(rowData.willBeReadyFrom)}
              </span>
            )}
          </Cell>
        </Column>

        {/* Patient Name */}
        <Column flexGrow={1} minWidth={100}>
          <HeaderCell>Ime pacijenta</HeaderCell>
          <Cell dataKey="patientName" verticalAlign="middle" />
        </Column>

        {/* Article Name */}
        <Column flexGrow={3} minWidth={200}>
          <HeaderCell>Naziv artikla</HeaderCell>
          <Cell verticalAlign="middle" fullText={true}>
            {(rowData: ArticleViewData) => {
              const expiringToday = isTherapyExpiringToday(
                rowData.willBeReadyTo
              );
              const expiringSoon = isTherapyExpiringSoon(rowData.willBeReadyTo);

              return (
                <div className={styles.articleNameCell}>
                  {rowData.articleName}
                  {(expiringToday || expiringSoon) && (
                    <span
                      className={styles.bellIconContainer}
                      title={
                        expiringToday
                          ? "Terapija ističe danas"
                          : "Terapija uskoro ističe"
                      }
                    >
                      <BellIcon
                        className={`${styles.bellIcon} ${
                          expiringToday
                            ? styles.bellIconToday
                            : styles.bellIconSoon
                        }`}
                      />
                    </span>
                  )}
                </div>
              );
            }}
          </Cell>
        </Column>

        {/* Therapy Taken */}
        <Column width={80} align="center">
          <HeaderCell>Preuzeto</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: ArticleViewData) => (
              <span className={styles.therapyStatus}>
                {rowData.therapyTaken ? "✓" : "✗"}
              </span>
            )}
          </Cell>
        </Column>

        {/* Quantity */}
        <Column width={80} align="center">
          <HeaderCell>Količina</HeaderCell>
          <Cell dataKey="quantity" verticalAlign="middle" />
        </Column>

        {/* RFZO Price */}
        <Column width={100} minWidth={80} align="right">
          <HeaderCell>RFZO Cena</HeaderCell>
          <Cell verticalAlign="middle">
            {(rowData: ArticleViewData) => {
              const price = rowData.rfzoPrice;
              const isNull = price === null || price === undefined;
              const tooltip = isNull
                ? "RFZO cena nije dostupna za ovaj artikal"
                : `RFZO cena: ${formatRFZOPrice(price)}`;

              return <span title={tooltip}>{formatRFZOPrice(price)}</span>;
            }}
          </Cell>
        </Column>
      </Table>
    </div>
  );
};

export default ArticleView;
